/**
 * CPA rule-management surface strings.
 *
 * Its own namespace rather than more keys under `settings`, matching the
 * per-feature split the rest of the portal uses (chat / eval / tasks / skills).
 *
 * `zh` is the reference shape — `en` is typed `typeof zh`, so a key added to one
 * locale and not the other fails the type check.
 */
export default {
  // ── Page chrome ──
  title: 'CPA',
  tabTree: '决策树',
  tabPrograms: '项目参数',
  tabRules: '规则',
  tabStacking: '叠加关系',
  tabVersions: '版本',

  // ── Decision-tree container ──
  treeTitle: '决策树',
  viewCanvas: '画布',
  viewOutline: '大纲',
  edit: '编辑',
  cancel: '取消',
  save: '保存',

  draftBannerTitle: '草稿只存在这个浏览器标签页里',
  draftBannerBody:
    '服务端还没有写问卷定义的接口（五个 JSON 是 Lambda 里从 classpath 读的），所以保存暂时不可用，取消或刷新都会丢弃。改动可以用来验证规则后果。',

  // ── Palette ──
  paletteQuestion: '＋ 问题',
  kindSingle: '单选题',
  kindSingleHint: '一组选项里选一个',
  kindMulti: '多选题',
  kindMultiHint: '可多选；答案存成数组，规则用 .includes()',
  kindNumber: '数值题',
  kindNumberHint: '数字输入，自动带 dataType: NUMBER',
  kindAmount: '金额题',
  kindAmountHint: '数值 + 单位 $K；单位只影响显示，不改存储值',
  kindPercent: '百分比题',
  kindPercentHint: '数值 + 单位 %，并设 scale 0.01 —— 输入 30 存成 0.3，规则要写 >= 0.3',
  kindSfdc: 'SFDC 账户',
  kindSfdcHint: '独立组件：拉我名下账户 / 手输 18 位 ID 并远程校验',
  kindText: '文本题',
  kindTextHint: '自由文本，无选项',
  paletteSubtree: '⊞ 子集',
  paletteRule: '＋ 规则',
  paletteDelete: '🗑 删除',
  paletteInsert: '插入到线上',
  paletteUndo: '↩ 撤销',
  tidyUp: '一键整理',
  tidyUpWithCount: '一键整理（复位 {{count}} 张手放的卡片）',

  hintInsert: '插入模式：点任意一条连线，新问题就接在那条线中间。',

  // ── Outline ──
  outlineFilterPlaceholder: '筛选问题',
  outlineCount: '{{count}} 个问题',
  outlineEmpty: '没有匹配的问题',
  outlineColQuestion: '问题',
  outlineColAnswer: '答案',
  outlineCombined: '组合值',
  outlineColLogic: '判断逻辑',
  outlineColNext: '下一题',
  outlineTerminal: '终点',
  outlineExpand: '展开',
  outlineCollapse: '收起',

  // ── Delete dialog ──
  delTitle: '删除「{{id}}」？',
  delConfirm: '删除',
  delRelink: '上游连线会改接到：',
  delRelinkNone: '（无，上游将失去出边）',
  delBrokenTitle: '{{count}} 条规则会失效',
  delBrokenBody:
    '这些规则读的是这道题的答案。删掉之后它们的表达式还在，但读到的是 undefined —— <strong>引擎不会报错，规则只是永远不再命中</strong>。',
  delColNode: '所在节点',
  delColKind: '类型',
  delColExpr: '表达式',
  delNoBroken: '没有规则读取这道题，删除不影响推荐',
  delOrphansTitle: '下游 {{count}} 个节点会一起删除',
  delOrphansBody: '删掉它之后，这些节点再也走不到，所以会一并移除：',

  // ── Connect dialog ──
  connTitle: '这条连线走哪个答案？',
  connConfirm: '连上',
  connPickLabel: '哪个答案走这条线？',
  connPickHint: '只列出还没有被其它条件边用掉的答案。',
  connPickPlaceholder: '选一个答案',
  connExprLabel: '条件表达式',
  connExprHint: '这道题没有可选答案（例如数值输入题）。',
  rewriteTitle: '原来的无条件连线会变成默认分支',
  rewriteBody:
    '引擎只要看到 <code>nextNodeId</code> 就不再看条件边，所以它会被改写成排在最后的 <code>true</code> 分支 —— <strong>不匹配新条件的答案行为不变。</strong>',

  // ── Insert dialog ──
  insTitle: '在这条连线上插入一道题？',
  insConfirm: '插入',
  insBody: '，新节点接在中间。',
  insNote: '新节点只有「是 / 否」两个选项，且没有任何规则读取它 —— 会被问，但暂时不影响任何推荐结果。',

  // ── Branch dialog ──
  brTitle: '从「{{id}}」拉一条新分支',
  brConfirm: '添加',
  brPickLabel: '哪个答案走这条新分支？',
  brPickHint: '只列出还没有被任何条件边用掉的答案。',
  brExprLabel: '分支条件',
  brExprHint: '这道题没有可用的选项值（例如数值输入题）。',
  brRewriteTitle: '这个节点现在是无条件连线，会被改写成条件边',
  brRewriteBody:
    '原来的连线会变成一条排在最后的 <code>true</code> 默认分支。<strong>凡是不匹配新条件的答案，行为与现在完全一致。</strong>',
  brNewNodeNote: '新节点默认是「终点节点」，带是/否两个选项、没有任何规则。',

  // ── UI-only palette buttons ──
  todoSubtreeTitle: '子集节点',
  todoHeader: '这个按钮现在只有外观，没有行为',
  todoSubtreeBody:
    '设计稿里有「子集」节点，存储模型里没有 —— 五份问卷彼此独立，没有「把一棵子树复用到另一棵里」的表达方式。要做这个按钮，服务端得先有可被引用的子树实体。',
  ok: '知道了',

  // ── 项目参数 (investment programs) ──
  programsCaption: '来自 investment program 表 · 比例单位 % / 年（一次性项目按支持年限分摊）· 金额单位 $',
  programsEdit: '编辑',
  programsCreate: '新建',
  programsSearch: '搜索项目、POC、SLA',
  programsMatches: '{{count}} 个匹配',
  programsLoading: '正在加载项目参数',
  programsEmpty: '没有项目',
  programsNoMatch: '没有匹配的项目',
  programsClearFilter: '清除筛选',
  programsLoadError: '项目参数加载失败',
  programsDenied: '只有 Deal Desk 管理员（admin 组）可以查看项目参数。',
  programsRetry: '重试',
  colPriority: '优先级',
  colName: '项目',
  colType: '类型',
  colCycle: '周期',
  colRatio: '折扣比例',
  colAmount: '金额',
  colPoc: 'POC',
  colSla: 'SLA',
  colQualifier: 'ARR 门槛',
  colExclusive: '互斥项目',
  colLinks: '链接',
  colUpdated: '更新时间',
  ppaNonPpa: '独立',
  ppaSceLedCrossService: 'SCE-led 跨服务 PPA',
  ppaSellerLedCrossService: 'Seller-led 跨服务 PPA',
  ppaSceLedServiceSpecific: 'SCE-led 单服务 PPA',
  ppaSellerLedServiceSpecific: 'Seller-led 单服务 PPA',
  ppaRPpa: 'R-PPA',
  cycleRecurring: '按年',
  cycleOneTime: '一次性',
  noLimit: '不限',
  tiered: '分档',
  linkWiki: 'Wiki',
  linkApply: '申请',
  prefsTitle: '偏好设置',
  prefsPageSize: '每页行数',
  prefsColumns: '显示列',
  prefsConfirm: '确定',

  // ── 叠加关系 (stacking matrix) ──
  stackingCaption: '来自每个项目的互斥名单（mutually_exclusive），按 global / 中国区分别判定；任一方列出对方即互斥，与推荐引擎一致',
  stackingSearch: '搜索项目',
  stackingRelationFilter: '只看关系',
  stackingRelationFilterPlaceholder: '全部关系',
  stackingNoMatch: '没有匹配的项目',
  stackingStackable: '可叠加',
  stackingExclusive: '互斥',
  stackingChinaOnly: '仅中国区可叠加',
  stackingGlobalOnly: '仅 global 可叠加',
  stackingSelf: '同一项目',
  stackingPairs: '{{count}} 对',
  stackingListedBy: '{{region}}：{{names}} 的互斥名单里列了对方',
  stackingRegionGlobal: 'global',
  stackingRegionChina: '中国区',

  // ── 规则 (recommendation-engine rules) ──
  rulesCaption: '推荐引擎如何从候选项目生成推荐方案（不含问卷的资格规则，见决策树）',
  rulesVersion: '规则版本 v{{version}}',
  rulesPublished: '发布于 {{date}}（{{by}}）',
  rulesEngineImplemented: '引擎已实现 · {{ref}}',
  rulesEnginePending: '引擎尚未实现这个版本',
  rulesLoadError: '规则加载失败',
  rulesFilter: '按项目筛选',
  rulesAllPrograms: '全部项目',
  rulesNoMatch: '没有涉及这个项目的规则',
  rulesLegendLive: '实时：读自项目参数',
  rulesLegendEngine: '引擎常量',
  rulesParamLive: '读自项目参数：{{source}}',

  // ── 项目参数编辑 ──
  editorCreateTitle: '新建项目',
  editorEditTitle: '编辑：{{name}}',
  editorBasics: '基本信息',
  editorNameFixed: '名称保存后不能再改：问卷、引擎和其他项目的互斥名单都按名称引用项目',
  editorNameLocked: '名称不能修改',
  editorPriorityHint: '数字越小越优先',
  editorDiscount: '折扣方式',
  discountRatio: '比例',
  discountAmount: '金额',
  discountTable: '档位表',
  editorMinRatio: '最小比例（%）',
  editorMaxRatio: '最大比例（%）',
  editorMinAmount: '最小金额（$）',
  editorMaxAmount: '最大金额（$）',
  editorTiers: '档位',
  editorTiersHint: 'ARR 区间（$，含下限不含上限），每年比例用逗号分隔，例如 3, 4, 5 表示第 1 / 2 / 3 年 3% / 4% / 5%',
  editorTierMin: 'ARR 下限',
  editorTierMax: 'ARR 上限',
  editorTierRatios: '每年比例（%）',
  editorAddTier: '添加档位',
  editorRemoveTier: '删除这一档',
  editorEligibility: '门槛与互斥',
  editorQualifierMin: '门槛下限（$，可空）',
  editorQualifierMax: '门槛上限（$，可空）',
  editorExclusiveGlobal: '互斥项目（global）',
  editorExclusiveChina: '互斥项目（中国区）',
  editorExclusiveHint: '引擎双向判断，只要一方列了对方就互斥，不必两边都填',
  editorContacts: '联系人与链接',
  editorOnePerLine: '每行一个',
  editorReview: '检查改动',
  editorBack: '返回修改',
  editorConfirmSave: '确认保存',
  editorLiveWarning: '保存后立即生效：下一次推荐就会用新的参数。',
  editorLiveWarningNew: '保存后项目立即出现在项目参数和叠加关系里；但只有问卷规则引用了它的名称，它才会被推荐（需要 SDE 修改问卷）。',
  editorNoChanges: '没有任何改动。',
  editorField: '字段',
  editorBefore: '原来',
  editorAfter: '改为',
  editorSaved: '已保存：{{name}}',
  diff_name: '名称',
  diff_priority: '优先级',
  diff_ppaType: '类型',
  diff_discountCycle: '周期',
  diff_discount: '折扣方式',
  diff_qualifier: '门槛',
  diff_exclusiveGlobal: '互斥（global）',
  diff_exclusiveChina: '互斥（中国区）',
  diff_contacts: 'POC',
  diff_sla: 'SLA',
  diff_wikis: 'Wiki',
  diff_applicationLinks: '申请链接',
  formRequired: '必填',
  formPriority: '请输入 0 或更大的整数',
  formNumber: '请输入数字',
  formRatioRange: '需要 0 ≤ 最小 ≤ 最大 ≤ 100',
  formMinMax: '最小值不能大于最大值',
  formTiersEmpty: '至少需要一档',
  formTier: '每一档都需要 下限 < 上限，并至少填一年的比例',
  formHttps: '链接需要以 https:// 开头',
  saveConflict: '这个项目在你打开之后被修改过，请关闭后刷新再改',
  saveDisabled: '这个环境还没有开放修改项目参数',
  saveInvalid: '服务端校验未通过',
  saveFailed: '保存失败',

  // ── 版本 ──
  versionsNewDraft: '新建草稿',
  versionsConfirm: '确认',
  versionsReason: '退回原因',
  versionsReference: '实现位置',
  versionsReferenceHint: 'CR 链接或 commit，例如 https://code.amazon.com/reviews/CR-123456',
  versionsConflict: '这个版本在你打开之后被修改过，请刷新后再操作',
  versionsOwnPublish: '不能发布自己编写的版本，请另一位管理员发布',
  versionStatus_draft: '草稿',
  versionStatus_in_review: '待审批',
  versionStatus_published: '已发布',
  versionStatus_archived: '已归档',
  versionAction_submit: '提交审批',
  versionAction_discard: '丢弃草稿',
  versionAction_publish: '发布',
  versionAction_reject: '退回修改',
  versionAction_implemented: '标记引擎已实现',
  versionConfirm_submit: '提交后草稿不能再编辑，等待另一位管理员发布或退回。',
  versionConfirm_discard: '丢弃后这个草稿会被删除，不能恢复。',
  versionConfirm_publish: '发布后这个版本成为当前规则，上一个已发布版本会归档。引擎行为不会自动改变，需要 SDE 实现后再标记。',
  versionConfirm_reject: '退回后版本回到草稿，作者可以修改后重新提交。',
  versionConfirm_implemented: '确认引擎代码已经实现了这个版本的规则。',
  rulesViewingVersion: '正在查看 v{{version}}（{{status}}），不是当前生效的规则。',
  rulesVersionPick: '选择版本',
  rulesGoToOpen: '去看 v{{version}}（未完成）',
  rulesBackToCurrent: '查看当前规则',

  // ── 规则编辑 ──
  editRulesStart: '编辑草稿',
  editRulesTitle: '编辑 v{{version}}（草稿）',
  editRulesCaption: '改完点保存。保存只改草稿，提交审批并发布后才成为当前规则；引擎由 SDE 实现。',
  editRulesSave: '保存草稿',
  editRulesNote: '这次改了什么',
  editRulesNoteHint: '会显示在版本说明里，审批人据此判断',
  editRulesFix: '有内容需要修改，见标红的地方',
  editRuleSectionTitle: '分段标题',
  editRuleSectionSummary: '分段说明',
  editRuleUntitled: '（未命名分段）',
  editRuleRemoveSection: '删除这个分段',
  editRuleTextHint: '用 {名字} 引用下面的参数，名字只能是字母和数字',
  editRuleRemove: '删除这条规则',
  editRuleParamNameLabel: '参数名',
  editRuleParamKind: '来源',
  editRuleKind_engine: '固定值',
  editRuleKind_program: '读项目参数',
  editRuleParamValue: '值，例如 $300K、3 年',
  editRuleParamProgram: '项目',
  editRuleParamField: '字段',
  editRuleField_qualifier_min: '门槛下限',
  editRuleField_qualifier_max: '门槛上限',
  editRuleRemoveParam: '删除参数',
  editRuleAddParam: '添加参数',
  editRuleProgramsLoading: '正在加载项目',
  editRuleProgramsError: '项目列表加载失败，选择项目的下拉框暂时为空',
  editRulePrograms: '涉及项目',
  editRuleAdd: '添加规则',
  editRuleAddSection: '添加分段',
  editRuleTable: '表格',
  editRuleRemoveRow: '删除这一行',
  editRuleAddRow: '添加一行',
  editRuleTitleRequired: '分段需要标题',
  editRuleTextRequired: '规则需要文字',
  editRuleParamName: '参数名只能是字母和数字',
  editRuleParamDuplicate: '参数名重复',
  editRulePlaceholder: '文字里引用了不存在的参数',
  editRuleParamMissing: '参数需要填值或选择项目',

  // ── 项目停用 / 启用 ──
  colStatus: '状态',
  statusActive: '启用',
  statusDisabled: '已停用',
  statusDisable: '停用',
  statusEnable: '启用',
  statusDisableTitle: '停用：{{name}}',
  statusEnableTitle: '启用：{{name}}',
  statusDisableBody: '停用后立即不再被推荐，即使问卷规则还引用它；项目参数、互斥关系和规则里的引用都保留，随时可以重新启用。',
  statusEnableBody: '启用后，问卷规则引用它时会重新参与推荐。',
  statusEditDisabled: '已停用的项目不能编辑，先启用',
};
